import { supportedLanguagesByLocaleCode } from './locales'
import { supportedRegionsByAlpha2RegionCode } from './regions'
import { LiveLanguageCodes } from './types'
import { isSupportedLanguage } from './utils'

/**
 * Picks the most relevant *live* locale for a given market (country),
 * preferring the viewer's own language over the market's primary language.
 *
 * Some markets have more than one supported locale — e.g. Spain (ES) has
 * both es-ES and en-ES. Callers otherwise tend to hard-map a market to its
 * primary-language locale (ES -> es-ES) and serve that to everyone, even to
 * English-speaking viewers. This resolves the market's candidate locales and
 * picks the best match for `preferredLanguages` (the viewer's ordered
 * language signals, e.g. navigator.languages or a parsed Accept-Language
 * header), falling back to the market's default language.
 *
 * Only locales that are live in the current environment are considered (via
 * isSupportedLanguage, which honours the production enabledLocales gate), so
 * a held-back locale is never returned in production. Returns undefined when
 * the market is unknown or has no live locale, letting callers fall back to
 * their default resolution.
 *
 * @param countryCode - alpha-2 or alpha-3 region code, e.g. 'ES' or 'ESP'
 * @param preferredLanguages - ordered language preferences; entries may be
 *   bare language codes ('en') or full locale codes ('en-GB')
 */
export function getMarketLocale(
  countryCode: string,
  preferredLanguages: string[] = [],
): LiveLanguageCodes | undefined {
  const region = findRegionByCountryCode(countryCode)
  if (!region) {
    return undefined
  }

  // The market's candidate locales that are live in this environment.
  const candidates = Object.values(supportedLanguagesByLocaleCode).filter(
    (locale) =>
      locale.country.alpha2 === region.alpha2 &&
      isSupportedLanguage(locale.localeCode),
  )

  if (candidates.length === 0) {
    return undefined
  }

  // Prefer a candidate whose language matches the viewer's, in preference
  // order. Match on the bare language code (e.g. 'en' from 'en-GB') so an
  // 'en-US' signal still selects the market's English locale.
  for (const preference of preferredLanguages) {
    const languageCode = toLanguageCode(preference)
    const match = candidates.find(
      (locale) => locale.languageCode === languageCode,
    )
    if (match) {
      return match.localeCode
    }
  }

  // No viewer preference matched — fall back to the market's primary
  // language (e.g. ES -> es-ES), then to any live candidate.
  const marketDefault = candidates.find(
    (locale) => locale.languageCode === region.defaultLanguageCode,
  )
  return (marketDefault ?? candidates[0]!).localeCode
}

function findRegionByCountryCode(countryCode: string) {
  const normalised = countryCode.toUpperCase()
  return Object.values(supportedRegionsByAlpha2RegionCode).find(
    (region) => region.alpha2 === normalised || region.alpha3 === normalised,
  )
}

function toLanguageCode(language: string): string {
  // Split on '-' or '_': some platforms emit underscore-separated tags
  // (e.g. 'en_GB') alongside the standard hyphenated 'en-GB'.
  return language.split(/[-_]/)[0]!.toLowerCase()
}
